"use client";

import { useMemo } from "react";
import type { CalendarFestival } from "@/lib/api/calendar";
import { getDaysInMonth, getFirstDayOfWeek, toDateString, TYPE_COLORS } from "./types";

interface CalendarGridProps {
  year: number;
  month: number;
  festivals: CalendarFestival[];
  onSelect: (date: string) => void;
}

export function CalendarGrid({ year, month, festivals, onSelect }: CalendarGridProps) {
  const today = toDateString(new Date());
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  // Build date → festivals map
  const festivalMap = useMemo(() => {
    const map: Record<string, CalendarFestival[]> = {};
    for (const f of festivals) {
      const day = f.date;
      if (!map[day]) map[day] = [];
      map[day].push(f);
    }
    return map;
  }, [festivals]);

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="w-full">
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-px mb-1">
        {dayLabels.map((d) => (
          <div
            key={d}
            className="text-center text-[10px] font-bold text-ish-text-muted py-1 uppercase tracking-widest"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day === null) {
            return <div key={`empty-${idx}`} className="aspect-square" />;
          }
          const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const dayFestivals = festivalMap[dateStr] || [];
          const isToday = dateStr === today;

          const hasFestival = dayFestivals.some((f) => f.type === "festival");
          const hasVrat = dayFestivals.some((f) => f.type === "vrat");
          const hasEclipse = dayFestivals.some((f) => f.type === "eclipse");

          return (
            <button
              type="button"
              key={dateStr}
              onClick={() => dayFestivals.length > 0 && onSelect(dateStr)}
              className={`relative aspect-square flex flex-col items-center justify-center rounded-xl transition-all
                ${dayFestivals.length > 0 ? "cursor-pointer hover:bg-ish-accent/5" : "cursor-default"}
                ${isToday ? "border-2 border-ish-accent bg-ish-accent/5" : "border border-transparent bg-black/[0.02]"}
              `}
            >
              <span
                className={`text-sm ${isToday ? "font-bold text-ish-accent" : "text-ish-dark font-medium"}`}
              >
                {day}
              </span>

              {/* Dots */}
              {dayFestivals.length > 0 && (
                <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {hasFestival && (
                    <span className={`w-1 h-1 rounded-full ${TYPE_COLORS.festival.dot}`} />
                  )}
                  {hasVrat && <span className={`w-1 h-1 rounded-full ${TYPE_COLORS.vrat.dot}`} />}
                  {hasEclipse && (
                    <span className={`w-1 h-1 rounded-full ${TYPE_COLORS.eclipse.dot}`} />
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 justify-center mt-6 text-[11px] font-medium text-ish-text-muted">
        {[
          { label: "Festival", color: TYPE_COLORS.festival.dot },
          { label: "Vrat", color: TYPE_COLORS.vrat.dot },
          { label: "Eclipse", color: TYPE_COLORS.eclipse.dot },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${item.color}`} />
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
}
